/**
 * ForcePasswordChangeGate (P1)
 *
 * Renders a full-screen blocking modal whenever the authenticated user's
 * JWT payload carries `password_reset_required === true` (set by the
 * admin-initiated immediate password reset). The user cannot dismiss the
 * modal or navigate elsewhere until they submit a new password that
 * passes backend strength validation.
 *
 * Why a full-screen overlay instead of a dedicated route?
 *   - We want it to appear on top of ANY route (dashboard, admin, etc.)
 *     the user happens to land on after login — no router-level rewiring.
 *   - The overlay keeps the underlying page inert but lets us drop the
 *     gate once the new password is set without a full reload.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { changeMyPassword } from '../../utils/usersApi';
import { Button } from '../ui/button';

const readUser = () => {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const ForcePasswordChangeGate = () => {
  const [needsReset, setNeedsReset] = useState(
    () => !!(readUser() && readUser().password_reset_required === true)
  );
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showPw, setShowPw] = useState(false);

  // Re-evaluate on storage changes (e.g. after login in another tab,
  // or after we clear the flag on success).
  useEffect(() => {
    const sync = () => {
      const u = readUser();
      setNeedsReset(!!(u && u.password_reset_required === true));
    };
    window.addEventListener('storage', sync);
    window.addEventListener('auth:user-updated', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('auth:user-updated', sync);
    };
  }, []);

  // Also recheck on every route change via a polling tick (cheap).
  useEffect(() => {
    if (!needsReset) {
      const t = setInterval(() => {
        const u = readUser();
        if (u && u.password_reset_required === true) setNeedsReset(true);
      }, 1500);
      return () => clearInterval(t);
    }
    return undefined;
  }, [needsReset]);

  const clearFlagLocally = useCallback(() => {
    try {
      const u = readUser();
      if (u) {
        u.password_reset_required = false;
        localStorage.setItem('user', JSON.stringify(u));
      }
    } catch { /* ignore */ }
    setNeedsReset(false);
    try { window.dispatchEvent(new Event('auth:user-updated')); } catch { /* ignore */ }
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (!currentPassword) return setError('Enter your current (temporary) password.');
    if (!newPassword || newPassword.length < 12) {
      return setError('New password must be at least 12 characters.');
    }
    if (newPassword === currentPassword) {
      return setError('New password must be different from the temporary password.');
    }
    if (newPassword !== confirmPassword) {
      return setError('Passwords do not match.');
    }
    setSubmitting(true);
    try {
      await changeMyPassword(currentPassword, newPassword);
      clearFlagLocally();
    } catch (err) {
      let msg = 'Could not change password. Please try again.';
      if (err?.status === 401) msg = 'Current password is incorrect.';
      else if (err?.message) msg = err.message;
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!needsReset) return null;

  return (
    <div
      className="force-password-gate-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="force-pw-title"
      data-testid="force-password-change-overlay"
      style={styles.overlay}
    >
      <div className="force-password-gate-card" style={styles.card} data-testid="force-password-change-modal">
        <div style={styles.header}>
          <span style={styles.icon} aria-hidden="true">🔐</span>
          <div>
            <h2 id="force-pw-title" style={styles.title} data-testid="force-password-change-title">
              Password Change Required
            </h2>
            <p style={styles.sub}>
              An administrator reset your password. Set a new one to continue.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={styles.body}>
          <label style={styles.label} htmlFor="fp-current">Current (temporary) password</label>
          <input
            id="fp-current"
            type={showPw ? 'text' : 'password'}
            autoComplete="current-password"
            autoFocus
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            disabled={submitting}
            style={styles.input}
            data-testid="force-current-password"
          />

          <label style={styles.label} htmlFor="fp-new">New password</label>
          <input
            id="fp-new"
            type={showPw ? 'text' : 'password'}
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={submitting}
            style={styles.input}
            data-testid="force-new-password"
          />

          <label style={styles.label} htmlFor="fp-confirm">Confirm new password</label>
          <input
            id="fp-confirm"
            type={showPw ? 'text' : 'password'}
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={submitting}
            style={styles.input}
            data-testid="force-confirm-password"
          />

          <label style={styles.showPwLabel}>
            <input
              type="checkbox"
              checked={showPw}
              onChange={(e) => setShowPw(e.target.checked)}
              disabled={submitting}
            />
            <span>Show passwords</span>
          </label>

          <div style={styles.hint}>
            Must contain ≥12 characters, an uppercase, lowercase, digit and special character.
          </div>

          {error && (
            <div style={styles.error} data-testid="force-password-error">{error}</div>
          )}

          <div style={styles.footer}>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="force-password-submit"
              style={{ width: '100%' }}
            >
              {submitting ? 'Changing…' : 'Change Password & Continue'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 100000,
    background: 'rgba(15, 23, 42, 0.72)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  card: {
    width: '100%',
    maxWidth: 480,
    background: '#ffffff',
    borderRadius: 14,
    boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.45)',
    overflow: 'hidden',
    border: '1px solid rgba(15, 23, 42, 0.08)',
  },
  header: {
    display: 'flex',
    gap: 14,
    padding: '20px 24px',
    background: 'linear-gradient(180deg, #fffbeb 0%, #fef3c7 100%)',
    borderBottom: '1px solid #fcd34d',
  },
  icon: { fontSize: 28, lineHeight: 1 },
  title: { margin: 0, fontSize: 18, fontWeight: 600, color: '#0f172a' },
  sub: { margin: '4px 0 0', fontSize: 13, color: '#78350f' },
  body: { padding: '20px 24px 4px', display: 'flex', flexDirection: 'column' },
  label: { fontSize: 13, fontWeight: 600, color: '#334155', margin: '10px 0 6px' },
  input: {
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    padding: '10px 12px',
    fontSize: 14,
    outline: 'none',
  },
  showPwLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 10,
    fontSize: 13,
    color: '#475569',
    cursor: 'pointer',
  },
  hint: { fontSize: 12, color: '#64748b', marginTop: 8, lineHeight: 1.5 },
  error: {
    marginTop: 12,
    background: '#fee2e2',
    border: '1px solid #fecaca',
    color: '#b91c1c',
    padding: '8px 12px',
    borderRadius: 8,
    fontSize: 13,
  },
  footer: { padding: '16px 0 20px' },
};

export default ForcePasswordChangeGate;
